import { useState, type FormEvent } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { KeyRound, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react'

export default function InvitePage() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const pwMatch = password === confirm
  const pwLong = password.length >= 8
  const hasUpper = /[A-Z]/.test(password)
  const hasLower = /[a-z]/.test(password)
  const hasDigit = /\d/.test(password)
  const isValid = pwMatch && pwLong && hasUpper && hasLower && hasDigit

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!isValid || !token) return

    setError(null)
    setIsSubmitting(true)

    try {
      const res = await fetch('/api/auth/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Fehler' }))
        throw new Error(data.detail || `Fehler ${res.status}`)
      }
      setSuccess(true)
      setTimeout(() => navigate(`/portal/${slug}/login`), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-red-200 rounded-xl p-6 max-w-md w-full text-center">
          <AlertCircle size={40} className="text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-1">Ungültiger Link</h1>
          <p className="text-gray-500 text-sm">Dieser Einladungslink ist ungültig oder abgelaufen.</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-green-200 rounded-xl p-6 max-w-md w-full text-center">
          <CheckCircle2 size={40} className="text-green-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-1">Passwort gesetzt!</h1>
          <p className="text-gray-500 text-sm">
            Sie werden zur Anmeldung weitergeleitet…
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-fom-500 rounded-xl grid place-items-center text-white text-lg font-extrabold">
              F
            </div>
            <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
              My<span className="font-normal text-gray-500">FOM</span>
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Einladung annehmen</h1>
          <p className="text-gray-500 text-sm mt-1">Bitte setzen Sie Ihr Passwort.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Neues Passwort
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm
                           focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none"
                placeholder="Mindestens 8 Zeichen"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Passwort bestätigen
            </label>
            <input
              id="confirm"
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none"
              placeholder="Passwort wiederholen"
            />
          </div>

          {/* Passwort-Regeln */}
          <div className="mb-6 space-y-1 text-xs">
            <PwRule ok={pwLong}>Mindestens 8 Zeichen</PwRule>
            <PwRule ok={hasUpper}>Mindestens ein Großbuchstabe</PwRule>
            <PwRule ok={hasLower}>Mindestens ein Kleinbuchstabe</PwRule>
            <PwRule ok={hasDigit}>Mindestens eine Zahl</PwRule>
            <PwRule ok={pwMatch && password.length > 0}>Passwörter stimmen überein</PwRule>
          </div>

          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="w-full btn btn-primary py-2.5 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Wird gesetzt…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <KeyRound size={16} />
                Passwort setzen
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

function PwRule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? 'text-green-600' : 'text-gray-400'}`}>
      <span className={`w-3 h-3 rounded-full border ${ok ? 'bg-green-500 border-green-500' : 'border-gray-300'}`} />
      {children}
    </div>
  )
}
